import apiHelper from "$lib/services/helper";
import {UserStore} from "$lib/stores";
import type {UserInfo} from "$lib/types/user";
import type {ChatInfo, ChatMessage} from "$lib/types";
import {UNEXPECTED_SERVER_RESPONSE} from "$lib/constants";
import { format, formatRelative } from "date-fns";
import locale from 'date-fns/locale/ru/index';

/**
 в данном файлике реализуем апи работы с Чатами
 */
export const ChatsApi = (userToken?: string) => {

  const API_NAME = "chats";
  const API_SUB_NAME = "chat-messages";

  const {get, post, put, del} = apiHelper(userToken);

  return {

    /**
     * Получить чат с пользователем
     * @param userId { number } ИД пользователя с кем начинаем чат
     */
    getChat: async function (userId: number): Promise<{err?: string, userChat?: ChatInfo}> {
      if (!userId) return { err: "userId is required"}

      let me: UserInfo | null = null;
      UserStore.subscribe((currentUser: UserInfo | null )=>{
        me = currentUser
      });

      if (!(me && me["id"])) return { err: "Current user not defined. Are you authorized?"}

      const params = {
        data: {
          whom: userId
        },
      }

      const { err, data} = await post(API_NAME, params);

      if (err) return { err };

      if (data.error) return { err: data.error };

      if (!data?.data) return { err: UNEXPECTED_SERVER_RESPONSE };

      const userChat =  {
        ...data?.data,
        users: data?.data?.users?.map((user: any) => {
          return {...user, user: user.id}
        }) || []
      };


      return { userChat };
    },

    /**
     * Получить список всех чатов пользователя
     */
    getChatList: async function(params = {}): Promise<{err?: string, chatList?: ChatInfo[]}> {

      let me: UserInfo | null = null;
      UserStore.subscribe((currentUser: UserInfo | null )=>{
        me = currentUser
      });

      if (!(me && me["id"])) return { err: "Current user not defined. Are you authorized?"}

      const { err, data } = await get(API_NAME, {
        populate: '*',
        ...params,
      });

      if (!data?.data || !( data?.data instanceof Array)) {
        return { err: UNEXPECTED_SERVER_RESPONSE}
      }


      /* каша - преобразуем ответ сервера в корректный тип */
      const chatList = data?.data.map((chat: any) => {

        const photo = chat?.lastMessage?.user?.photo;

        if(photo) {
          photo.thumbnail = (photo?.formats?.thumbnail) ? photo?.formats?.thumbnail : photo;
        }

        chat.lastMessage.createdAt = formatRelative(new Date(chat.lastMessage.createdAt), new Date(), { locale });

        chat.user = chat.users.map((user: any) => {
          if (user.user.photo)
          {
            user.user.photo.thumbnail = user.user.photo?.formats?.thumbnail || user.user.photo;
            delete user.user.photo.formats;
          }
          user = {...user.user, user: user.id}
          return user;
        })[0]; // по факту юзер всегда сейчас один, для удобства сделаем chat.users[0] => chat.user
        return chat;
      })

      return { err, chatList };

    },

    /**
     * Получить сообщения чата по id чата
     * @param chatId
     */
    getChatMessages: async function(chatId: number): Promise<{err?: string, messages?: ChatMessage[]}> {
      let me: UserInfo | null = null;
      UserStore.subscribe((currentUser: UserInfo | null )=>{
        me = currentUser
      });

      if (!(me && me["id"])) return { err: "Current user not defined. Are you authorized?"}
      const params = {
        filters: {
          chat: {
            id: {
              $eq: chatId
            }
          }
        }
      }
      const { err, data } = await get(`${API_SUB_NAME}?populate=user.photo`, params);

      if (err) {
        return { err }
      }

      if (!data?.data && !(data?.data instanceof Array))
        throw new Error("Unexpected server response");

      let messages = data?.data.reverse();

      try {
        messages = messages.map((m: ChatMessage) => {
          m.createdAt = format(new Date(m.createdAt), 'HH:mm dd.MM.yyyy');
          m.my = (me?.id == m?.user?.id)
          return m;
        });
      }
      catch(e: any){
        console.log(e);
        return { err: e.err }
      }

      return { err, messages }
    },

    postMessage: async function(chatId: number, message: string): Promise<{err?: string, message?: ChatMessage}>{
      if (!chatId) return { err: "chatId is required"};
      if (!message) return { err: "message is empty"};

      const params = {
        data: {
          chatId,
          text: message
        }
      }

      const { err, data } = await post(API_SUB_NAME, params);

      if (err) {
        return { err };
      }

      if (!data?.data) return { err: "Unexpected server response"};

      return { err, message: data?.data }
    },

    addUserToTheChat: async function(chatId: number): Promise<{err?: string, chat?: ChatInfo}> {
      if (!chatId) return { err: "chatId is required"};

      const params = {
        data: {
          action: 'add user',
        }
      }

      const { err, data } = await put(`${API_NAME}/${chatId}`, params);

      if (err) return { err };

      if (data.error) return { err: data.error };

      if (!data?.data) return { err: UNEXPECTED_SERVER_RESPONSE };

      return { err, chat: data };
    },

    deleteMessage: async (id: number): Promise<{ err?: string, data?: any }> => {
      const { err, data } = await del(`${API_SUB_NAME}/${id}`);

      if (err) return { err };

      if (!data?.data) {
        return { err: UNEXPECTED_SERVER_RESPONSE};
      }

      return { err, data };
    },

    blockUser: async (chatId: number, userId: number): Promise<{err?: string, data?: any}> => {
      if (!chatId) return { err: "chatId is required"};

      const params = {
        data: {
          action: 'block user',
          whom: userId,
        }
      }

      const { err, data } = await put(`${API_NAME}/${chatId}`, params);

      if (err) return { err };

      if (data.error) return { err: data.error };

      if (!data?.data) return { err: UNEXPECTED_SERVER_RESPONSE };

      return { err, data };
    },

    unblockUser: async (chatId: number, userId: number): Promise<{err?: string, data?: any}> => {
      if (!chatId) return { err: "chatId is required"};

      const params = {
        data: {
          action: 'unblock user',
          whom: userId,
        }
      }

      const { err, data } = await put(`${API_NAME}/${chatId}`, params);

      if (err) return { err };

      if (data.error) return { err: data.error };

      if (!data?.data) return { err: UNEXPECTED_SERVER_RESPONSE };

      return { err, data };
    },

    getChatById: async (chatId: number, params = {}): Promise<{err?: string, data?: any}> => {
      const { err, data } = await get(`${API_NAME}/${chatId}`, params);

      if (err) return { err };

      if (!data?.data) return { err: UNEXPECTED_SERVER_RESPONSE };

      return { err, data };
    },

    getUnreadChats: async (): Promise<{err?: string, data?: any}> => {
      const { err, data } = await get(`unread-chats`);

      if (err) return { err };

      if (!data?.data) return { err: UNEXPECTED_SERVER_RESPONSE };

      return { err, data: data?.data };
    },

    getChatUnreadMessages: async (): Promise<{err?: string, data?: any}> => {
      const { err, data } = await get(`chat-message-unreads`);

      if (err) return { err };

      if (!data?.data) return { err: UNEXPECTED_SERVER_RESPONSE };

      return { err, data: data?.data };
    }
  }
}
